<template lang="pug">
  .deals-page
    .category-fallback.main-container-wide
      Breadcrumbs(
        v-if="!$rogue.appContext && $route.query.q"
        :links="[{name:__('Search results for: {query}', {query: $route.query.q})}]"
      )
      Breadcrumbs(
        v-else-if="!$rogue.appContext"
        :category-id="id"
      )
      .category-content(ref="categoryContentEl")
        h1 {{category ? category.name : __('Search results for: {query}', {query: $route.query.q})}}
        .top-line(:style="{ visibility: isLoading ? 'hidden' : 'visible' }")
          span(v-html="__('<strong>Showing {start}-{end}</strong> of {total} Products', counts)")
          RogueButton.mobile-filter(@click="showFacets(true)") Filter
          RogueSelect.desktop-sort(:options="sortOptions" :modelValue="$route.query.sort" @update:modelValue="changeSort($event)")
        FiltersActive(:filters="filters")
        .mobile-filter-overlay(
          :class="uiStore.overlay === 'mobileFilters' && 'show'"
          @click="showFacets(false)"
        )
        .category-main
          filters.facets(
            :filters="filters"
            :order="filterOrder"
            :loading="isLoading"
            :class="{ open: uiStore.overlay === 'mobileFilters'}"
            @closed="showFacets(false)"
          )
          .products-wrapper
            hover-card-placeholder(v-if="isLoading" v-for="x in CARD_COUNT.xlarge" :key="x")
            .no-content(v-else-if="filteredProducts.length === 0")
              h3 No Results
            hover-card(
              v-for="(product, index) in filteredProducts"
              :product="product"
              :key="product.productId"
              :position="index"
            )
            h2(v-show="filteredProducts.length === 0") {{ __('No Results') }}
        Pagination.pagination(:page-number="currentPage" :total-pages="pageCount")
</template>
<script setup lang="ts">
import { computed, ref, onServerPrefetch, onMounted, watch, useTemplateRef } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { allowScroll, preventScroll } from 'sisu/js/body-scroll'
import { useMQ } from 'sisu/js/mq'
import { useHead } from '@unhead/vue'
import metadata from '../../helpers/metadata'
import type { IPartialPublicProduct } from './helpers/lib/build-facets'
import { useRogue } from '@/js/services/Rogue'
import { useCategoryStore } from '@/js/stores/categoryStore'
import { useHttpStore } from '@/js/stores/httpStore'
import { useUIStore } from '@/js/stores/uiStore'
import { usePromoStore } from '@/js/stores/promoStore'
import { buildFacets, backfillCategoryAttribute } from '@/js/pages/category/helpers'
import { updateFacetRoutes } from '@/js/pages/category/helpers/lib/update-facet-routes'
import FiltersActive from '@/js/pages/category/components/filters-active.vue'
import Filters from '@/js/pages/category/components/filters.vue'
import HoverCard from '@/js/pages/category/components/hover-card.vue'
import HoverCardPlaceholder from '@/js/pages/category/components/hover-card-placeholder.vue'
import Pagination from '@/js/pages/category/components/pagination.vue'
import { CARD_COUNT } from '@/js/stores/productConstants'
import type { IReflektionFacet, IReflektionFacetValue } from '@/js/services/rfk/types'

type FallbackProduct = (IPartialPublicProduct & { facets?: { [key: string]: string[] } })

const rogue = useRogue()
const route = useRoute()
const router = useRouter()
const categoryStore = useCategoryStore()
const httpStore = useHttpStore()
const promoStore = usePromoStore()
const uiStore = useUIStore()
const mq = useMQ()

const filteredProducts = ref<FallbackProduct[]>([])
const filters = ref<{[key: string]: IReflektionFacet}>({})
const filterOrder: string[] = ['promo_status', 'deal_type', 'category', 'deal_status', 'shipping', 'manufacturer', 'pb']
const isLoading = ref(true)
const sortOptions = [
  { label: 'Featured', value: '' },
  { label: 'Price: Low to High', value: 'price.asc' },
  { label: 'Price: High to Low', value: 'price.desc' },
]
const counts = ref<{
  start?: number,
  end?: number,
  total?: number,
}>({})
const pageCount = ref(1)
const currentPage = ref(1)

const categoryContent = useTemplateRef<HTMLElement>('categoryContentEl')

let products: FallbackProduct[] = []

const cache = computed(() => httpStore.cache)

const categories = computed(() => categoryStore.categories)

const id = computed(() => route.meta.id)

const ssrCacheKey = id.value
  ? `rfk-fallback-?categoryId=${id.value}`
  : route.query.q
    ? `rfk-fallback-?q=${encodeURIComponent(route.query.q as string)}`
    : null
isLoading.value = !ssrCacheKey || !httpStore.cache[ssrCacheKey]

const category = computed(() => categories.value[id.value!])

const lastUpdate = computed(() => promoStore.lastUpdate)

watch(() => route.query, () => {
  // If mobile filters are open, we don't want to run this
  // The customer should be able to select/deselect multiple
  // filters before the update runs in the `mobileFacetsOpen`
  // watcher
  update()
  if (categoryContent.value && categoryContent.value.getBoundingClientRect().top < 0) {
    categoryContent.value.scrollIntoView({
      block: 'start', inline: 'start', behavior: mq.value === 'mobile' ? 'smooth' : 'auto',
    })
  }
}, {
  immediate: false,
})

watch(() => route.query.q, () => {
  if (!id.value) {
    load()
  }
}, {
  immediate: false,
})

watch(id, () => {
  load()
}, {
  immediate: false,
})

watch(lastUpdate, () => {
  rebuildFacets()
  update()
})

onServerPrefetch(async () => {
  await load()
})

async function load() {
  isLoading.value = true

  if (!id.value && !route.query.q) {
    router.push('/')

    return
  }

  const query = id.value ? `?categoryId=${id.value}` : `?q=${encodeURIComponent(route.query.q as string)}`
  const cacheKey = `rfk-fallback-${query}`

  let response = cache.value[cacheKey] as {products: IPartialPublicProduct[]}

  if (!response) {
    response = await rogue.fetch(`/svc_content/catalog/search/${query}`)
    httpStore.setCache(cacheKey, response)
  }

  if (id.value && !category.value) {
    await categoryStore.load(id.value as number, rogue)
  }

  products = response.products

  rebuildFacets()

  isLoading.value = false
  update()
}

onMounted(() => {
  load()
  rogue.log.debug('Loading Category Fallback')
})

function rebuildFacets() {
  backfillCategoryAttribute(products || [])
  // Cache this products facets (which may be different than attrs) so we can match them later
  products?.forEach(p => {
    p.facets = {}
    buildFacets(filterOrder, [p], rogue).forEach(facet => {
      p.facets![facet.key] = p.facets![facet.key] || []
      facet.value.map(val => p.facets![facet.key].push(val.urlKey))
    })
  })
}

function showFacets(show: boolean) {
  if (show) {
    uiStore.showOverlay('mobileFilters', rogue)
    preventScroll()
  } else {
    uiStore.hideOverlay()
    allowScroll()
  }
}

function changeSort(sortBy: string) {
  const newQuery = Object.assign({}, route.query, { page_number: undefined, sort: sortBy })
  router.push({ query: newQuery })
}

function update() {
  if (isLoading.value) {
    return
  }

  const matchQuery = (key: string, product: FallbackProduct) => {
    const qsVal = route.query[key]
    if (qsVal === undefined) {
      return true
    }

    const toCheck = Array.isArray(qsVal) ? qsVal : [qsVal]

    return toCheck.some(val => (product.facets![key] || []).indexOf(val as string) !== -1)
  }

  const sort = route.query.sort || 'featured'

  const matchingProducts = products
    .filter(p => filterOrder.every(attr => matchQuery(attr, p)))
    .sort((a, b) => {
      const aPrice = Number(`${a.special_price || a.price}`)
      const bPrice = Number(`${b.special_price || b.price}`)
      if (sort === 'price.asc') {
        return aPrice - bPrice
      }
      if (sort === 'price.desc') {
        return bPrice - aPrice
      }

      return products.indexOf(a) - products.indexOf(b)
    })

  currentPage.value = parseInt(route.query.page_number as string || '1', 10)
  const first = (currentPage.value - 1) * 40

  filteredProducts.value = matchingProducts.slice(first, first + 40)
  pageCount.value = Math.ceil(matchingProducts.length / 40)

  counts.value = {
    start : first + 1,
    end   : first + filteredProducts.value.length,
    total : matchingProducts.length,
  }

  const productFilters: {[key: string]: IReflektionFacet} = {}
  filterOrder.forEach(filter => {
    if (route.query[filter]) {
      const productsToUseForFacet = products
        .filter(p => filterOrder.filter(f => f !== filter).every(attr => matchQuery(attr, p)))

      productFilters[filter] = buildFacets([filter], productsToUseForFacet, rogue)[0]
    } else {
      productFilters[filter] = buildFacets([filter], matchingProducts, rogue)[0]
    }
  })

  updateFacetRoutes(productFilters, route.query as { [key: string]: string | string[] })

  let changed = false
  const newFilters = JSON.parse(JSON.stringify(route.query))
  Object.entries(route.query)
    .filter(([key]) => filterOrder.includes(key))
    .forEach(([key, appliedVals]) => {
      appliedVals = Array.isArray(appliedVals) ? appliedVals : [appliedVals]
      newFilters[key] = appliedVals.filter(val => productFilters[key]?.value.some((f: IReflektionFacetValue) => f.urlKey === val))
      if (appliedVals.length !== newFilters[key].length) {
        changed = true
      }
    })

  if (changed) {
    router.replace({ query: newFilters })
  }

  filters.value = productFilters
}

useHead(metadata({
  title  : id.value ? category.value.name : `${rogue.__('Search results for:')} ${route.query.q}`,
  robots : Object.keys(filters.value).length > 1 ? 'noindex' : 'all',
}, rogue))
</script>
<style lang="scss" scoped>
.deal-types {
  display: flex;
  margin-bottom: $space-xs;
  width: 100%;
  padding-bottom: $space-xs;
  overflow-x: auto;

  @include all-desktop() {
    padding-bottom: 0;
    margin-bottom: $space-s;
    overflow-x: hidden;
  }

  .deal-type {
    margin-left: -1px;

    &:first-child {
      margin-left: 0;
    }
  }

  .button {
    font-weight: $weight-bold;
    text-transform: uppercase;
    white-space: nowrap;
    font-family: $font-condensed;
    padding: $space-xs;

    &:hover {
      border-color: black;
      border-width: 1px;
      z-index: 1;
    }
  }
}

.mobile-filter {
  padding: 14px $space-m;
}

.mobile-filter-overlay {
  position: fixed;
  opacity: 0;
  pointer-events: none;
  background-color: rgba(0, 0, 0, 0.3);
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;

  &.show {
    opacity: 1;
    pointer-events: all;
  }
}

.desktop-sort {
  display: none;
}

.header-contents {
  .deal-type {
    text-align: center;

    .type-img, p {
      display: none;
    }
  }
}

.top-line {
  font-size: $size-xxs;
  display: flex;
  margin-bottom: $space-s;
  align-items: center;
  justify-content: space-between;
}

.category-main {
  display: flex;
}

.facets {
  @include all-desktop() {
    flex-basis: 200px;
    flex-grow: 0;
    flex-shrink: 0;
    margin-right: 32px;
  }

  @include not-desktop() {
    position: fixed;
    top: 0;
    left: calc(-100% - 24px);
    z-index: 2;
    height: 100vh;
    width: 100%;
    max-width: 350px;
    transition: left 500ms ease-in-out;

    &.open {
      left: 0;
    }
  }
}

.products-wrapper {
  display: flex;
  flex-wrap: wrap;
  flex-grow: 2;

  & > div {
    flex-basis: 50%;

    @media screen and (min-width: 480px) {
      flex-basis: 33.333%;
    }

    @media screen and (min-width: 960px) {
      flex-basis: 25%;
    }
  }
}

.pagination {
  margin-bottom: $space-m;
}

.deals-page {
  margin-top: $space-m;
}

@include all-desktop() {
  .deals-page .top-line .mobile-filter {
    display: none;
  }

  .desktop-sort {
    display: block;
    margin-bottom: 0;
  }

  .header-contents {
    display: flex;
  }

  .head-block {
    flex-basis: 200px;
    flex-grow: 0;
    flex-shrink: 0;
    margin-right: 32px;

    p {
      font-size: $size-xs;
    }
  }
  .pagination {
    margin-left: 232px;
  }
}

</style>
